import type { BlueprintConnection, BlueprintDocument, Diagnostic } from '../api/types';
import { parsePointer } from './jsonPointer';

export function connectionKey(
  connection: Pick<BlueprintConnection, 'sourceId' | 'targetId' | 'type'>,
): string {
  return `${connection.sourceId}->${connection.targetId}:${connection.type}`;
}

/** Where a diagnostic belongs in the designer (spec 6.3.7). */
export type DiagnosticTarget =
  | {
      kind: 'resource';
      resourceId: string;
      /** `properties`, `providerOverrides` or another part of the resource. */
      area: 'properties' | 'providerOverrides' | 'other';
      /** For providerOverrides: the provider key. */
      provider?: string;
      /** Path inside the properties (or the provider's overrides) object; [] is the object itself. */
      field: string[] | null;
    }
  | { kind: 'connection'; connectionKey: string; index: number }
  | { kind: 'parameter'; name: string }
  | { kind: 'secret'; name: string }
  | { kind: 'general' };

/**
 * Maps a diagnostic's JSON Pointer (into `doc`, the document that was validated) to its element.
 * Resource and connection targets are resolved to IDs and keys so they survive later edits.
 */
export function mapDiagnostic(diagnostic: Diagnostic, doc: BlueprintDocument): DiagnosticTarget {
  const segments = parsePointer(diagnostic.path ?? '');
  const [head, second, third, ...rest] = segments;

  if (head === 'resources' && second !== undefined && /^\d+$/.test(second)) {
    const resource = doc.resources[Number(second)];
    if (resource) {
      if (third === 'properties') {
        return { kind: 'resource', resourceId: resource.id, area: 'properties', field: rest };
      }
      if (third === 'providerOverrides' && rest.length > 0) {
        const [provider, ...field] = rest;
        return { kind: 'resource', resourceId: resource.id, area: 'providerOverrides', provider, field };
      }
      return { kind: 'resource', resourceId: resource.id, area: 'other', field: null };
    }
  }

  if (head === 'connections' && second !== undefined && /^\d+$/.test(second)) {
    const index = Number(second);
    const connection = doc.connections?.[index];
    if (connection) return { kind: 'connection', connectionKey: connectionKey(connection), index };
  }

  // Feature spec 17, phase 2: a network request's diagnostics belong to its source app.
  if (head === 'networkRequests' && second !== undefined && /^\d+$/.test(second)) {
    const request = doc.networkRequests?.[Number(second)];
    if (request && doc.resources.some((r) => r.id === request.sourceId))
      return { kind: 'resource', resourceId: request.sourceId, area: 'other', field: null };
  }

  if (head === 'parameters' && second !== undefined) {
    return { kind: 'parameter', name: second };
  }

  if (head === 'secrets' && second !== undefined) {
    return { kind: 'secret', name: second };
  }

  if (diagnostic.resourceId && doc.resources.some((r) => r.id === diagnostic.resourceId)) {
    return { kind: 'resource', resourceId: diagnostic.resourceId, area: 'other', field: null };
  }

  return { kind: 'general' };
}

export interface Counts {
  errors: number;
  warnings: number;
}

export interface MappedDiagnostic {
  diagnostic: Diagnostic;
  target: DiagnosticTarget;
}

export interface DiagnosticIndex {
  all: MappedDiagnostic[];
  byResource: Map<string, MappedDiagnostic[]>;
  byConnection: Map<string, MappedDiagnostic[]>;
  byParameter: Map<string, MappedDiagnostic[]>;
  bySecret: Map<string, MappedDiagnostic[]>;
  general: MappedDiagnostic[];
}

export function indexDiagnostics(
  diagnostics: readonly Diagnostic[],
  doc: BlueprintDocument,
): DiagnosticIndex {
  const index: DiagnosticIndex = {
    all: [],
    byResource: new Map(),
    byConnection: new Map(),
    byParameter: new Map(),
    bySecret: new Map(),
    general: [],
  };
  const push = (map: Map<string, MappedDiagnostic[]>, key: string, item: MappedDiagnostic) => {
    const list = map.get(key);
    if (list) list.push(item);
    else map.set(key, [item]);
  };
  for (const diagnostic of diagnostics) {
    const target = mapDiagnostic(diagnostic, doc);
    const item = { diagnostic, target };
    index.all.push(item);
    switch (target.kind) {
      case 'resource':
        push(index.byResource, target.resourceId, item);
        break;
      case 'connection':
        push(index.byConnection, target.connectionKey, item);
        break;
      case 'parameter':
        push(index.byParameter, target.name, item);
        break;
      case 'secret':
        push(index.bySecret, target.name, item);
        break;
      default:
        index.general.push(item);
    }
  }
  return index;
}

/**
 * The Problems window's Where column (spec 6.8.10): `web · image`, `web · providerOverrides.azure.sku`,
 * `uploads → orders-fn · options.functionName`, `parameters · env`, `secrets · api-key`, else the pointer.
 */
export function describeWhere({ diagnostic, target }: MappedDiagnostic, fallback: string): string {
  switch (target.kind) {
    case 'resource': {
      const field =
        target.area === 'providerOverrides'
          ? ['providerOverrides', target.provider ?? '', ...(target.field ?? [])].filter(Boolean).join('.')
          : (target.field ?? []).join('.');
      return field ? `${target.resourceId} · ${field}` : target.resourceId;
    }
    case 'connection': {
      const [source, rest = ''] = target.connectionKey.split('->');
      const targetId = rest.slice(0, rest.lastIndexOf(':'));
      const field = parsePointer(diagnostic.path ?? '')
        .slice(2)
        .join('.');
      return field ? `${source} → ${targetId} · ${field}` : `${source} → ${targetId}`;
    }
    case 'parameter':
      return `parameters · ${target.name}`;
    case 'secret':
      return `secrets · ${target.name}`;
    default:
      return diagnostic.path || (diagnostic.resourceId ?? fallback);
  }
}

export function countSeverities(items: readonly MappedDiagnostic[] | undefined): Counts {
  const counts = { errors: 0, warnings: 0 };
  for (const { diagnostic } of items ?? []) {
    if (diagnostic.severity === 'error') counts.errors++;
    else counts.warnings++;
  }
  return counts;
}

/** rjsf ErrorSchema shape: nested objects with `__errors` arrays. */
export interface FieldErrorTree {
  __errors?: string[];
  [key: string]: FieldErrorTree | string[] | undefined;
}

/**
 * Builds the form error tree for one resource's properties form, or for one provider's
 * overrides form when `provider` is given.
 */
export function fieldErrors(
  items: readonly MappedDiagnostic[] | undefined,
  provider?: string,
): FieldErrorTree {
  const root: FieldErrorTree = {};
  for (const { diagnostic, target } of items ?? []) {
    if (target.kind !== 'resource' || target.field === null) continue;
    if (provider === undefined ? target.area !== 'properties' : target.provider !== provider) continue;
    let node = root;
    for (const segment of target.field) {
      node[segment] ??= {};
      node = node[segment] as FieldErrorTree;
    }
    (node.__errors ??= []).push(diagnostic.message);
  }
  return root;
}

/** The DOM id rjsf gives a field: `<idPrefix>_<segment>_<segment>`. */
export function formFieldId(idPrefix: string, field: readonly string[]): string {
  return [idPrefix, ...field].join('_');
}

/** Diagnostics whose path is the given pointer or lies below it (request-body paths in dialogs). */
export function diagnosticsAt(diagnostics: readonly Diagnostic[] | undefined, pointer: string): Diagnostic[] {
  return (diagnostics ?? []).filter((d) => d.path === pointer || d.path?.startsWith(pointer + '/'));
}
